import type { components, paths } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type PlayerListResponse = components['schemas']['PlayerListResponse'];
export type Player = PlayerListResponse['data'][number];
export type PlayerFixture = Player['fixtures'][number];
export type PlayerPriceChange = NonNullable<Player['priceChange']>;
export type PlayerListQuery = NonNullable<
  paths['/league/players']['get']['parameters']['query']
>;
export type PlayerPosition = 'GK' | 'DEF' | 'MID' | 'FWD';
export type PlayerSortBy = NonNullable<PlayerListQuery['sortBy']>;
export type PlayerSortDirection = NonNullable<PlayerListQuery['sortDirection']>;

export type GetPlayersOptions = {
  accessToken?: string;
  cursor?: string;
  limit?: number;
  ids?: string;
  optaIds?: string;
  position?: PlayerPosition;
  teamId?: string;
  minGameweek?: number;
  maxGameweek?: number;
  minPrice?: number;
  maxPrice?: number;
  search?: string;
  sortBy?: PlayerSortBy;
  sortDirection?: PlayerSortDirection;
  include?: string;
  /** Comma-separated enrichments to exclude, e.g. 'predictions' to skip prediction enrichment for callers that only need raw player data. */
  exclude?: string;
  /**
   * Season year. Defaults to the current season. Player ids are re-minted
   * each season, so a past-season lookup must go through `optaIds` (stable
   * across seasons) rather than a current-season `ids` value.
   */
  season?: number;
};

const buildQuery = ({
  cursor,
  limit,
  ids,
  optaIds,
  position,
  teamId,
  minGameweek,
  maxGameweek,
  minPrice,
  maxPrice,
  search,
  sortBy,
  sortDirection,
  include,
  exclude,
  season
}: GetPlayersOptions): PlayerListQuery => ({
  ...(cursor !== undefined && { after: cursor }),
  ...(limit !== undefined && { limit }),
  ...(ids !== undefined && { ids }),
  ...(optaIds !== undefined && { optaIds }),
  ...(position !== undefined && { position }),
  ...(teamId !== undefined && { teamId }),
  ...(minGameweek !== undefined && { minGameweek }),
  ...(maxGameweek !== undefined && { maxGameweek }),
  ...(minPrice !== undefined && { minPrice }),
  ...(maxPrice !== undefined && { maxPrice }),
  ...(search !== undefined && search.length > 0 && { search }),
  ...(sortBy !== undefined && { sortBy }),
  ...(sortDirection !== undefined && { sortDirection }),
  ...(include !== undefined && { include }),
  ...(exclude !== undefined && { exclude }),
  ...(season !== undefined && { season })
});

export const fetchPlayers = async (
  options: GetPlayersOptions = {}
): Promise<PlayerListResponse> => {
  const { accessToken } = options;
  const { data, error } = await ffhClient().GET('/league/players', {
    params: { query: buildQuery(options) },
    ...(accessToken ? { headers: authHeader(accessToken) } : {})
  });

  if (error) throw new Error('Failed to load players');
  return data;
};
